import { useEffect, useState } from "react";
import { studioAuth, fetchMe } from "../lib/studioApi";
import { adminToken } from "../lib/api";

/**
 * Reusable Studio member access check — used to gate any member-only tool
 * (Chords, Lyrics, Planner, Melody Generator, future tools). Designed to be
 * the single source of truth so a future "free trial" / "guest preview" /
 * "team seat" change happens in ONE place.
 *
 * Returns:
 *   loading   – true while we're confirming the token with the backend
 *   hasAccess – boolean, final decision
 *   reason    – when hasAccess=false, one of:
 *                 'unauthenticated'      → no token at all; redirect to login
 *                 'token_invalid'        → token didn't validate against /me
 *                 'deactivated'          → admin set active=false
 *                 'inactive_subscription'→ subscription user, but Stripe is past_due/canceled
 *               when hasAccess=true: 'admin' | 'invited' | 'subscriber'
 *   user      – the /me payload when known, null otherwise
 *   isAdmin   – admin bearer is present (admins always pass)
 *
 * Access policy (matches the spec):
 *   1. Admin bearer token present                 → ALWAYS pass.
 *   2. Studio JWT + /me confirms user.active && (
 *        accountSource === 'admin'                 → invited / private / test
 *        OR subscriptionActive === true            → Stripe Membership / Plus
 *      )                                          → pass
 *   3. Anything else                              → deny with a reason.
 */
export function useStudioAccess() {
  const [state, setState] = useState({
    loading: true,
    hasAccess: false,
    reason: "unauthenticated",
    user: null,
    isAdmin: false,
  });

  useEffect(() => {
    let cancelled = false;

    const adminBearer = adminToken?.get?.();
    if (adminBearer) {
      setState({
        loading: false,
        hasAccess: true,
        reason: "admin",
        user: null,
        isAdmin: true,
      });
      return () => {
        cancelled = true;
      };
    }

    const jwt = studioAuth.getToken();
    if (!jwt) {
      setState({
        loading: false,
        hasAccess: false,
        reason: "unauthenticated",
        user: null,
        isAdmin: false,
      });
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const me = await fetchMe();
        if (cancelled) return;
        if (!me?.active) {
          setState({
            loading: false,
            hasAccess: false,
            reason: "deactivated",
            user: me,
            isAdmin: false,
          });
          return;
        }
        const isAdminCreated = me.accountSource === "admin";
        const isSubscriber = me.accountSource === "subscription";
        if (isAdminCreated) {
          setState({
            loading: false,
            hasAccess: true,
            reason: "invited",
            user: me,
            isAdmin: false,
          });
          return;
        }
        if (isSubscriber && me.subscriptionActive === true) {
          setState({
            loading: false,
            hasAccess: true,
            reason: "subscriber",
            user: me,
            isAdmin: false,
          });
          return;
        }
        setState({
          loading: false,
          hasAccess: false,
          reason: "inactive_subscription",
          user: me,
          isAdmin: false,
        });
      } catch {
        if (cancelled) return;
        // /me threw → token is stale/invalid.
        studioAuth.clear?.();
        setState({
          loading: false,
          hasAccess: false,
          reason: "token_invalid",
          user: null,
          isAdmin: false,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
